import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { X, Plus, Tag as TagIcon, Hash } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useTags } from "@/hooks/useTags";
import { isKnowledgeBaseDocumentEntity } from "@/lib/tagEntityTypes";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { tagsService } from "@/services/tagsService";
import { invalidateKnowledgeBaseItem } from "@/queries/knowledgeBaseDocuments";
import type { Tag } from "@shared/schema";

interface TagWithUsage extends Tag {
  usageCount?: number;
}

interface TagPickerProps {
  entityType: string;
  entityId: string;
  selectedTags?: Tag[];
  onTagsChange?: (tags: Tag[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

async function loadKbDocumentTags(
  entityId: string,
  allTags: Tag[]
): Promise<Tag[]> {
  const item = await knowledgeBaseService.getKnowledgeBaseItem(entityId);
  if (!item) return [];
  const tagIds = new Set((item.tagIds ?? []).map(String));
  return allTags.filter((t) => tagIds.has(String(t.id)));
}

export function TagPicker({
  entityType,
  entityId,
  selectedTags: externalTags,
  onTagsChange,
  placeholder = "Add tags...",
  disabled = false,
}: TagPickerProps) {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const isKbDocument = isKnowledgeBaseDocumentEntity(entityType);
  const { data: allTags = [] } = useTags();

  const entityTagsQueryKey = isKbDocument
    ? (["knowledgebase-item-tags", entityId] as const)
    : (["/api/entities", entityType, entityId, "tags"] as const);

  const { data: fetchedEntityTags = [] } = useQuery<Tag[]>({
    queryKey: entityTagsQueryKey,
    queryFn: async () => {
      if (!entityId) return [];
      if (isKbDocument) {
        return loadKbDocumentTags(entityId, allTags);
      }
      const res = await fetch(
        `/api/entities/${entityType}/${entityId}/tags`,
        { credentials: "include" }
      );
      if (!res.ok) return [];
      return res.json();
    },
    enabled:
      !!entityType &&
      !!entityId &&
      !externalTags &&
      (!isKbDocument || allTags.length > 0),
  });

  const selectedTags = externalTags ?? fetchedEntityTags;

  const suggestions = allTags.filter((tag) => {
    const alreadySelected = selectedTags.some((t) => t.id === tag.id);
    const matchesInput = tag.name.toLowerCase().includes(inputValue.toLowerCase());
    return !alreadySelected && matchesInput;
  });

  const refreshAfterKbTagChange = async () => {
    await queryClient.invalidateQueries({ queryKey: entityTagsQueryKey });
    await invalidateKnowledgeBaseItem(queryClient, entityId);
    await queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
    const fresh = await loadKbDocumentTags(entityId, allTags);
    queryClient.setQueryData(entityTagsQueryKey, fresh);
    onTagsChange?.(fresh);
  };

  const addTagMutation = useMutation({
    mutationFn: async (tagData: { tagId?: string; tagName?: string }) => {
      if (isKbDocument) {
        const item = await knowledgeBaseService.getKnowledgeBaseItem(entityId);
        if (!item) throw new Error("Document not found");
        let tagIds = [...(item.tagIds ?? [])];
        if (tagData.tagId) {
          if (!tagIds.includes(tagData.tagId)) tagIds.push(tagData.tagId);
        } else if (tagData.tagName?.trim()) {
          let tag = await tagsService.getTagByName(tagData.tagName.trim());
          if (!tag) {
            tag = await tagsService.createTag({ name: tagData.tagName.trim() });
          }
          if (!tagIds.includes(tag.id)) tagIds.push(tag.id);
        }
        await knowledgeBaseService.updateKnowledgeBaseItem(entityId, { tagIds });
        return;
      }
      return apiRequest(
        "POST",
        `/api/entities/${entityType}/${entityId}/tags`,
        tagData
      );
    },
    onSuccess: async () => {
      if (isKbDocument) {
        await refreshAfterKbTagChange();
      } else {
        await queryClient.refetchQueries({
          queryKey: ["/api/entities", entityType, entityId, "tags"],
          type: "active",
          exact: true,
        });
        await queryClient.refetchQueries({ queryKey: ["/api/tags"], type: "active" });
        const freshTags =
          queryClient.getQueryData<Tag[]>([
            "/api/entities",
            entityType,
            entityId,
            "tags",
          ]) || [];
        onTagsChange?.(freshTags);
      }
      setInputValue("");
      setShowSuggestions(false);
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: async (tagId: string) => {
      if (isKbDocument) {
        const item = await knowledgeBaseService.getKnowledgeBaseItem(entityId);
        if (!item) throw new Error("Document not found");
        const tagIds = (item.tagIds ?? []).filter((id) => id !== tagId);
        await knowledgeBaseService.updateKnowledgeBaseItem(entityId, { tagIds });
        return;
      }
      await apiRequest(
        "DELETE",
        `/api/entities/${entityType}/${entityId}/tags/${tagId}`
      );
    },
    onSuccess: async () => {
      if (isKbDocument) {
        await refreshAfterKbTagChange();
      } else {
        await queryClient.refetchQueries({
          queryKey: ["/api/entities", entityType, entityId, "tags"],
          type: "active",
          exact: true,
        });
        await queryClient.refetchQueries({ queryKey: ["/api/tags"], type: "active" });
        const freshTags =
          queryClient.getQueryData<Tag[]>([
            "/api/entities",
            entityType,
            entityId,
            "tags",
          ]) || [];
        onTagsChange?.(freshTags);
      }
    },
  });

  const handleAddTag = (tag: Tag) => {
    if (entityType && entityId) {
      addTagMutation.mutate({ tagId: tag.id });
    }
  };

  const handleCreateNewTag = () => {
    if (!inputValue.trim()) return;
    if (entityType && entityId) {
      addTagMutation.mutate({ tagName: inputValue.trim() });
    }
  };

  const handleRemoveTag = (tagId: string) => {
    if (entityType && entityId) {
      removeTagMutation.mutate(tagId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions.length > 0) {
        handleAddTag(suggestions[0]);
      } else if (inputValue.trim()) {
        handleCreateNewTag();
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setInputValue("");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedTags.map((tag) => (
          <Badge
            key={tag.id}
            variant="secondary"
            className="gap-1 pr-1"
            style={
              tag.color
                ? { backgroundColor: tag.color + "20", borderColor: tag.color }
                : undefined
            }
            data-testid={`tag-badge-${tag.id}`}
          >
            <Hash className="h-3 w-3" />
            {tag.name}
            <button
              onClick={() => handleRemoveTag(tag.id)}
              disabled={disabled || removeTagMutation.isPending}
              className="ml-1 rounded-full hover-elevate p-0.5"
              data-testid={`button-remove-tag-${tag.id}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>

      <div className="relative">
        <div className="relative">
          <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="pl-9"
            data-testid="input-tag-search"
          />
        </div>

        {showSuggestions && (inputValue || suggestions.length > 0) && (
          <div
            ref={suggestionsRef}
            className="absolute z-50 w-full mt-1 bg-card border rounded-md shadow-lg max-h-60 overflow-y-auto"
            data-testid="tag-suggestions"
          >
            {suggestions.length > 0 ? (
              <div className="p-1">
                {suggestions.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => handleAddTag(tag)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover-elevate active-elevate-2 text-left"
                    data-testid={`suggestion-tag-${tag.id}`}
                  >
                    <Hash
                      className="h-4 w-4"
                      style={{ color: tag.color || undefined }}
                    />
                    <span className="flex-1">{tag.name}</span>
                    {(tag as TagWithUsage).usageCount !== undefined &&
                      (tag as TagWithUsage).usageCount! > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {(tag as TagWithUsage).usageCount}{" "}
                          {(tag as TagWithUsage).usageCount === 1
                            ? "use"
                            : "uses"}
                        </span>
                      )}
                  </button>
                ))}
              </div>
            ) : inputValue.trim() ? (
              <button
                onClick={handleCreateNewTag}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover-elevate active-elevate-2 text-left"
                data-testid="button-create-tag"
              >
                <Plus className="h-4 w-4" />
                <span>Create "{inputValue.trim()}"</span>
              </button>
            ) : null}
          </div>
        )}
      </div>

      {addTagMutation.isPending && (
        <p className="text-xs text-muted-foreground">Adding tag...</p>
      )}
    </div>
  );
}
